import { useEffect, useMemo, useState } from "react";
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap } from "@vis.gl/react-google-maps";
import { MapPin, Users } from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAP_ID = "jayek_drivers_map";

// Bike/motorcycle teardrop marker — green when available, amber when busy
function DriverPin({ status }) {
  const color = status === "available" ? "#00A85A" : status === "busy" ? "#E89B27" : "#9A9A9A";
  return (
    <div style={{ position: "relative", width: 44, height: 52, filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.25))" }}>
      <div style={{ position: "absolute", inset: 0, background: color, borderRadius: "50% 50% 50% 0", transform: "rotate(-45deg)", border: "3px solid #FFFFFF" }} />
      <div style={{ position: "absolute", top: 6, left: "50%", transform: "translateX(-50%)", width: 28, height: 28, background: "#FFFFFF", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/><path d="M15 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/></svg>
      </div>
    </div>
  );
}

// Auto-fit bounds to include all drivers
function FitBounds({ points }) {
  const map = useMap();
  const pointsKey = JSON.stringify(points);
  useEffect(() => {
    if (!map || !points.length || !window.google) return;
    if (points.length === 1) {
      map.panTo(points[0]);
      map.setZoom(13);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, pointsKey]);
  return null;
}

export default function DriversMap({ drivers }) {
  const [selected, setSelected] = useState(null);

  // Keep only drivers who shared a location AND are not offline
  const online = useMemo(() => {
    return (drivers || []).filter(
      (d) => d.current_lat && d.current_lng && d.driver_status && d.driver_status !== "offline"
    );
  }, [drivers]);

  const center = online.length
    ? { lat: online[0].current_lat, lng: online[0].current_lng }
    : { lat: 24.7136, lng: 46.6753 }; // Riyadh fallback

  return (
    <div className="bg-white rounded-2xl border border-[#EAEAEA] overflow-hidden" data-testid="drivers-live-map">
      <div className="px-4 py-3 border-b border-[#F0F0F0] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#E05D3A]" />
          <h3 className="font-bold text-[14px] text-[#1A1A1A]">موقع المناديب المتصلين</h3>
        </div>
        <div className="flex items-center gap-2 text-[12px] text-[#6B6B6B]">
          <Users className="w-3.5 h-3.5" />
          <span>
            متصل: <b className="text-[#00A85A]">{online.length}</b> / {drivers.length}
          </span>
        </div>
      </div>
      <div style={{ height: 360 }}>
        {online.length === 0 ? (
          <div className="h-full w-full flex flex-col items-center justify-center text-center p-6 bg-[#FAFAFA]">
            <MapPin className="w-10 h-10 text-[#BDBDBD]" />
            <p className="mt-2 text-sm text-[#6B6B6B]">لا يوجد مناديب متصلين حالياً</p>
            <p className="mt-1 text-xs text-[#9A9A9A]">سيظهرون على الخريطة فور اتصالهم</p>
          </div>
        ) : !GOOGLE_MAPS_API_KEY ? (
          <div className="h-full w-full flex items-center justify-center text-sm text-[#6B6B6B] bg-[#FAFAFA]">
            خدمة الخرائط غير متاحة
          </div>
        ) : (
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY} language="ar" region="SA">
            <Map
              defaultCenter={center}
              defaultZoom={12}
              mapId={MAP_ID}
              gestureHandling="greedy"
              streetViewControl={false}
              mapTypeControl={false}
              fullscreenControl={false}
              clickableIcons={false}
              style={{ width: "100%", height: "100%" }}
            >
              <FitBounds points={online.map((d) => ({ lat: d.current_lat, lng: d.current_lng }))} />
              {online.map((d) => (
                <AdvancedMarker
                  key={d.user_id}
                  position={{ lat: d.current_lat, lng: d.current_lng }}
                  onClick={() => setSelected(d)}
                >
                  <DriverPin status={d.driver_status} />
                </AdvancedMarker>
              ))}
              {selected && (
                <InfoWindow
                  position={{ lat: selected.current_lat, lng: selected.current_lng }}
                  pixelOffset={[0, -46]}
                  onCloseClick={() => setSelected(null)}
                >
                  <div className="min-w-[160px] text-end" dir="rtl">
                    <p className="font-bold text-[14px]">{selected.name}</p>
                    <p className="text-[12px] text-[#6B6B6B]">
                      {selected.driver_status === "available" ? "🟢 متاح" : "🟡 مشغول"}
                    </p>
                    {selected.phone && <p className="text-[12px] mt-1">📱 {selected.phone}</p>}
                    <p className="text-[11px] text-[#6B6B6B] mt-1">
                      🚗 {selected.vehicle_type === "motorcycle" ? "دراجة نارية" : selected.vehicle_type === "car" ? "سيارة" : "دراجة"}
                    </p>
                    {(selected.active_count > 0) && (
                      <p className="text-[11px] mt-1 text-[#E05D3A] font-semibold">
                        {selected.active_count} طلب نشط
                      </p>
                    )}
                  </div>
                </InfoWindow>
              )}
            </Map>
          </APIProvider>
        )}
      </div>
      <div className="px-4 py-2 border-t border-[#F0F0F0] flex items-center gap-4 text-[11px] text-[#6B6B6B]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#00A85A]"></span>متاح</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#E89B27]"></span>مشغول</span>
        <span className="text-[10px] text-[#9A9A9A] ms-auto">يحدّث كل 15 ثانية</span>
      </div>
    </div>
  );
}
